/**
 * ☰ Admin › Usage — who uses the app, on what, from where, and for how long.
 *
 * `docs/plans/usage-tracking.md`. One row per user seen in the window; a click
 * opens that user's devices and latest sessions under the row. The three times
 * are the tracker's states — Active (input in the last 5 min), Watching
 * (visible, no input), Background (hidden) — and are never added together on
 * screen, because "time in the app" means a different thing for each.
 *
 * The table scrolls sideways inside itself rather than squeezing seven columns
 * onto a phone; the panel around it never scrolls horizontally.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { plainText } from "@qufolio/screener-grid";

import {
  fetchUsageUser,
  fetchUsageUsers,
  type UsageDetail,
  type UsageUser,
} from "../api/activity";
import { deviceLabel, formatDuration, formatWhen, placeLabel, screenLabel } from "../lib/activity";
import { AdminVisitors } from "./AdminVisitors";

const WINDOWS = [7, 30, 90, 365] as const;

const BAR: React.CSSProperties = { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" };
const NOTE: React.CSSProperties = { color: "var(--qf-text-secondary)", fontSize: 12 };
const MUTED: React.CSSProperties = { color: "var(--qf-text-muted)", fontSize: 12 };
const ERROR: React.CSSProperties = { ...NOTE, color: "var(--qf-negative, #d66)" };
const SCROLL: React.CSSProperties = { overflowX: "auto", maxWidth: "100%" };
const TABLE: React.CSSProperties = { borderCollapse: "collapse", width: "100%", fontSize: 12 };
const TH: React.CSSProperties = {
  textAlign: "left",
  fontWeight: 500,
  color: "var(--qf-text-muted)",
  padding: "4px 8px",
  borderBottom: "1px solid var(--qf-border)",
  whiteSpace: "nowrap",
  cursor: "pointer",
  userSelect: "none",
};
const TD: React.CSSProperties = { padding: "4px 8px", whiteSpace: "nowrap", verticalAlign: "top" };
const NUM: React.CSSProperties = { ...TD, textAlign: "right", fontVariantNumeric: "tabular-nums" };
const INPUT: React.CSSProperties = {
  background: "var(--qf-surface-alt)",
  color: "var(--qf-text)",
  border: "1px solid var(--qf-border)",
  borderRadius: 4,
  padding: "3px 8px",
  fontSize: 12,
};
const SUB: React.CSSProperties = {
  margin: "6px 0 2px",
  fontSize: 11,
  textTransform: "uppercase",
  letterSpacing: 0.6,
  color: "var(--qf-text-muted)",
};

type SortKey = "lastOpened" | "devices" | "sessions" | "activeSeconds" | "watchingSeconds" | "backgroundSeconds";

const COLUMNS: { key: SortKey; label: string; title?: string }[] = [
  { key: "lastOpened", label: "Last opened" },
  { key: "devices", label: "Devices" },
  { key: "sessions", label: "Sessions", title: "Each app open in a tab is one session" },
  { key: "activeSeconds", label: "Active", title: "Visible, with input in the last 5 minutes" },
  { key: "watchingSeconds", label: "Watching", title: "Visible, no input for 5 minutes" },
  { key: "backgroundSeconds", label: "Bkgd", title: "Tab hidden or minimised" },
];

function who(u: Pick<UsageUser, "displayName" | "email" | "userKey">): string {
  return u.displayName || u.email || u.userKey;
}

/**
 * Two views (`docs/plans/site-visits.md`): **Users**, the signed-in accounts
 * below, and **Visitors**, every page view whether signed in or not.
 */
export function AdminUsage() {
  const [view, setView] = useState<"users" | "visitors">("users");
  const tab = (active: boolean): React.CSSProperties => ({
    ...INPUT,
    background: active ? "var(--qf-selection)" : "transparent",
    color: active ? "var(--qf-text)" : "var(--qf-text-secondary)",
    cursor: "pointer",
  });
  return (
    <>
      <div role="tablist" aria-label="Usage view" style={{ display: "inline-flex", gap: 2, marginBottom: 8 }}>
        <button type="button" role="tab" aria-selected={view === "users"} style={tab(view === "users")} onClick={() => setView("users")}>
          Users
        </button>
        <button type="button" role="tab" aria-selected={view === "visitors"} style={tab(view === "visitors")} onClick={() => setView("visitors")}>
          Visitors
        </button>
      </div>
      {view === "users" ? <UsersView /> : <AdminVisitors />}
    </>
  );
}

function UsersView() {
  const [days, setDays] = useState<number>(30);
  const [users, setUsers] = useState<UsageUser[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<SortKey>("lastOpened");
  const [open, setOpen] = useState<string | null>(null);

  const reload = useCallback(() => {
    setError(null);
    fetchUsageUsers(days)
      .then(setUsers)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, [days]);
  useEffect(reload, [reload]);

  const shown = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const rows = (users ?? []).filter(
      (u) =>
        !needle ||
        [u.email, u.displayName, u.userKey, u.brand].some((v) => v?.toLowerCase().includes(needle)),
    );
    return [...rows].sort((a, b) =>
      sort === "lastOpened"
        ? (b.lastOpened ?? "").localeCompare(a.lastOpened ?? "")
        : (b[sort] as number) - (a[sort] as number),
    );
  }, [users, search, sort]);

  return (
    <>
      <div style={BAR}>
        <input
          style={{ ...INPUT, flex: "1 1 160px", minWidth: 0 }}
          placeholder="Search email, name, brand…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search users"
        />
        <select
          style={INPUT}
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          aria-label="Window"
        >
          {WINDOWS.map((d) => (
            <option key={d} value={d}>
              Last {d} days
            </option>
          ))}
        </select>
        <button type="button" style={INPUT} onClick={reload}>
          Refresh
        </button>
      </div>

      {error && <div style={ERROR}>{plainText(error)}</div>}
      {users === null && !error && <div style={MUTED}>Loading…</div>}
      {users !== null && shown.length === 0 && (
        <div style={NOTE}>{users.length === 0 ? "Nobody has opened the app in this window." : "No match."}</div>
      )}

      {shown.length > 0 && (
        <div style={SCROLL}>
          <table style={TABLE}>
            <thead>
              <tr>
                <th style={{ ...TH, cursor: "default" }}>User</th>
                {COLUMNS.map((c) => (
                  <th
                    key={c.key}
                    style={{
                      ...TH,
                      textAlign: c.key === "lastOpened" ? "left" : "right",
                      color: sort === c.key ? "var(--qf-text)" : TH.color,
                    }}
                    title={c.title ? `${c.title} — click to sort` : "Click to sort"}
                    onClick={() => setSort(c.key)}
                  >
                    {c.label}
                    {sort === c.key ? " ▾" : ""}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {shown.map((u) => (
                <UserRows
                  key={u.userKey}
                  user={u}
                  days={days}
                  open={open === u.userKey}
                  onToggle={() => setOpen(open === u.userKey ? null : u.userKey)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {users !== null && users.length > 0 && (
        <div style={MUTED}>
          {users.length} user{users.length === 1 ? "" : "s"} · times in your clock · sessions older
          than 90 days are kept as daily totals, so a longer window counts their time but not their
          devices.
        </div>
      )}
    </>
  );
}

function UserRows({
  user,
  days,
  open,
  onToggle,
}: {
  user: UsageUser;
  days: number;
  open: boolean;
  onToggle: () => void;
}) {
  return (
    <>
      <tr
        onClick={onToggle}
        style={{ cursor: "pointer", background: open ? "var(--qf-selection)" : undefined }}
        aria-expanded={open}
      >
        <td style={TD} title={`${user.email ?? ""} · ${user.userKey}`}>
          {open ? "▾ " : "▸ "}
          {who(user)}
          {user.brand && <span style={MUTED}> · {user.brand}</span>}
        </td>
        <td style={TD}>{formatWhen(user.lastOpened)}</td>
        <td style={NUM}>{user.devices}</td>
        <td style={NUM}>{user.sessions}</td>
        <td style={NUM}>{formatDuration(user.activeSeconds)}</td>
        <td style={NUM}>{formatDuration(user.watchingSeconds)}</td>
        <td style={NUM}>{formatDuration(user.backgroundSeconds)}</td>
      </tr>
      {open && (
        <tr>
          <td colSpan={7} style={{ ...TD, whiteSpace: "normal", paddingLeft: 20 }}>
            <Detail userKey={user.userKey} days={days} />
          </td>
        </tr>
      )}
    </>
  );
}

/** One user's devices and latest sessions — also the shape `/activity/me` returns. */
export function Detail({ userKey, days }: { userKey: string; days: number }) {
  const [detail, setDetail] = useState<UsageDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    fetchUsageUser(userKey, days)
      .then((d) => alive && setDetail(d))
      .catch((e: unknown) => alive && setError(e instanceof Error ? e.message : String(e)));
    return () => {
      alive = false;
    };
  }, [userKey, days]);

  if (error) return <div style={ERROR}>{plainText(error)}</div>;
  if (!detail) return <div style={MUTED}>Loading…</div>;
  return <DetailTables detail={detail} capped={days > 90} />;
}

export function DetailTables({ detail, capped }: { detail: UsageDetail; capped?: boolean }) {
  return (
    <>
      <div style={SUB}>Devices{capped ? " · last 90 days" : ""}</div>
      {detail.devices.length === 0 ? (
        <div style={NOTE}>No sessions in detail for this window.</div>
      ) : (
        <div style={SCROLL}>
          <table style={TABLE}>
            <thead>
              <tr>
                <th style={{ ...TH, cursor: "default" }}>Device</th>
                <th style={{ ...TH, cursor: "default" }}>Last place</th>
                <th style={{ ...TH, cursor: "default" }}>First seen</th>
                <th style={{ ...TH, cursor: "default" }}>Last seen</th>
                <th style={{ ...TH, cursor: "default", textAlign: "right" }}>Sessions</th>
                <th style={{ ...TH, cursor: "default", textAlign: "right" }}>Active</th>
              </tr>
            </thead>
            <tbody>
              {detail.devices.map((d) => (
                <tr key={d.deviceId}>
                  <td style={TD} title={d.deviceId}>
                    {deviceLabel(d)}
                  </td>
                  <td style={TD}>{placeLabel(d.lastCity, d.lastCountry) || "—"}</td>
                  <td style={TD}>{formatWhen(d.firstSeen)}</td>
                  <td style={TD}>{formatWhen(d.lastSeen)}</td>
                  <td style={NUM}>{d.sessions}</td>
                  <td style={NUM}>{formatDuration(d.activeSeconds)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {detail.sessions.length > 0 && (
        <>
          <div style={SUB}>Latest sessions</div>
          <div style={SCROLL}>
            <table style={TABLE}>
              <thead>
                <tr>
                  <th style={{ ...TH, cursor: "default" }}>Started</th>
                  <th style={{ ...TH, cursor: "default" }}>Ended</th>
                  <th style={{ ...TH, cursor: "default" }}>Device</th>
                  <th style={{ ...TH, cursor: "default" }}>Place</th>
                  <th
                    style={{ ...TH, cursor: "default" }}
                    title="Screen · pixel ratio (scaling × zoom) · smallest window the session had"
                  >
                    Screen
                  </th>
                  <th style={{ ...TH, cursor: "default", textAlign: "right" }}>Active</th>
                  <th style={{ ...TH, cursor: "default", textAlign: "right" }}>Watching</th>
                  <th style={{ ...TH, cursor: "default", textAlign: "right" }}>Bkgd</th>
                </tr>
              </thead>
              <tbody>
                {detail.sessions.map((s) => (
                  <tr key={`${s.deviceId}-${s.startedAt}`}>
                    <td style={TD}>{formatWhen(s.startedAt)}</td>
                    <td style={TD} title={s.endedAt ? "Tab closed" : "Last report"}>
                      {s.endedAt ? formatWhen(s.endedAt) : <span style={MUTED}>{formatWhen(s.lastBeatAt)}</span>}
                    </td>
                    <td style={TD}>{deviceLabel(s)}</td>
                    <td style={TD}>{placeLabel(s.city, s.country) || "—"}</td>
                    <td style={TD}>{screenLabel(s) || "—"}</td>
                    <td style={NUM}>{formatDuration(s.activeSeconds)}</td>
                    <td style={NUM}>{formatDuration(s.watchingSeconds)}</td>
                    <td style={NUM}>{formatDuration(s.backgroundSeconds)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={MUTED}>An ended time in grey is the last report — the tab closed without saying so.</div>
        </>
      )}
    </>
  );
}
